import 'twin.macro';
import React, { useState } from 'react';
import Button from 'components/button/Button';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import useCampaignValues from 'hooks/useCampaignValues';
import { useMutation } from 'hooks/sympl-mutation';
import useLiveAdsUpdate from 'hooks/useLiveAdsUpdate';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import { AdVariant } from 'types/adEditorTypes';
import {
  ACK_NOTICE,
  LEAVE_LIVE_ADS,
  TRANSLATE_AD_TEXTS,
} from 'graphql/campaign-values/mutations';
import {
  liveAdsMessage,
  fixedPhrasesNotice,
  restartNotice,
  textsLanguageMessage,
} from 'utils/liveAdsMessage';

/** The free texts "Translate these texts" covers (X-2); the server translates the same ones. */
const hasFreeText = (ad: AdVariant) =>
  [
    ad.text,
    ad.headline,
    ad.description,
    ad.free_text,
    ad.subtext,
    ad.banner_title,
  ].some((t) => typeof t === 'string' && t.trim() !== '') ||
  (ad.benefits ?? []).some((b) => b.trim() !== '');

/**
 * SYM-6351. The messages above the ads (scenes 7, 8, 12f): live ads that still show an old job
 * title or location, ad texts in the campaign's previous language, and the one-time notice after
 * an automatic restart. Every choice goes to the server; the words come from liveAdsMessage.ts.
 */
const CampaignValuesMessages = ({
  campaignLanguage,
  translatedUnsaved,
  onTranslated,
}: {
  campaignLanguage?: string;
  /** Translations are on the ads but not saved yet (the editor clears the notice on save). */
  translatedUnsaved: boolean;
  onTranslated: () => void;
}) => {
  const { state, vacancyId, refetch } = useCampaignValues();
  const { variants, applyToVariants, refreshTemplatePreview } =
    useAdEditorContext();
  const { addToast } = useToastNotifications();
  const [busy, setBusy] = useState<string | null>(null);

  const updateLiveAds = useLiveAdsUpdate(vacancyId);
  const [leaveLiveAds] = useMutation<{}, { vacancyId: number; input: {} }>(
    LEAVE_LIVE_ADS
  );
  const [ackNotice] = useMutation<
    {},
    { vacancyId: number; notice: string; input: {} }
  >(ACK_NOTICE);
  const [translate] = useMutation<
    {
      translateAdTexts: {
        language_id: number;
        translated_to?: string;
        ad_variants: Record<number, Partial<AdVariant>>;
      };
    },
    { vacancyId: number; input: { ad_variant_ids?: number[] } }
  >(TRANSLATE_AD_TEXTS);

  if (!state || !vacancyId) return null;

  const run = async (
    key: string,
    action: () => Promise<unknown>,
    failed: string
  ) => {
    setBusy(key);
    try {
      await action();
      await refetch();
    } catch (_) {
      addToast({ type: ToastTypes.ERROR, description: failed });
    } finally {
      setBusy(null);
    }
  };

  const liveAds = liveAdsMessage(state);
  const textAds = variants.filter(
    (ad) => ad.id && !ad.isDeleted && hasFreeText(ad)
  );
  const language = textsLanguageMessage(
    state,
    campaignLanguage,
    textAds.length
  );
  const notice = restartNotice(state);
  const phrases = fixedPhrasesNotice(state);

  if (!liveAds && !language && !notice && !phrases) return null;

  return (
    <div
      tw="flex flex-col gap-3 mb-4 w-full max-w-xl mx-auto"
      data-testid="campaign-values-messages"
    >
      {notice && (
        <Box>
          <p tw="text-sm font-semibold text-gray-900">{notice}</p>
          <div>
            <Button
              variant="secondary"
              loading={busy === 'notice'}
              onClick={() =>
                run(
                  'notice',
                  () =>
                    ackNotice({
                      variables: {
                        vacancyId,
                        notice: 'restart-updated',
                        input: {},
                      },
                    }),
                  'Could not close this message.'
                )
              }
            >
              OK
            </Button>
          </div>
        </Box>
      )}

      {liveAds && (
        <Box>
          <p tw="text-sm font-semibold text-gray-900">{liveAds.title}</p>
          <p tw="text-sm text-gray-700">{liveAds.body}</p>
          <div tw="flex gap-2">
            <Button
              loading={busy === 'update'}
              onClick={() =>
                run(
                  'update',
                  () => updateLiveAds(),
                  'The live ads could not be updated.'
                )
              }
            >
              {liveAds.updateLabel}
            </Button>
            <Button
              variant="secondary"
              loading={busy === 'leave'}
              onClick={() =>
                run(
                  'leave',
                  () => leaveLiveAds({ variables: { vacancyId, input: {} } }),
                  'Could not close this message.'
                )
              }
            >
              {liveAds.leaveLabel}
            </Button>
          </div>
        </Box>
      )}

      {language && translatedUnsaved && (
        <Box>
          <p tw="text-sm font-semibold text-gray-900">
            Translated texts are not saved yet
          </p>
          <p tw="text-sm text-gray-700">
            Check them and press Save. If you leave without saving, the ads keep
            their old texts.
          </p>
        </Box>
      )}

      {language && !translatedUnsaved && (
        <Box>
          <p tw="text-sm font-semibold text-gray-900">{language.title}</p>
          <p tw="text-sm text-gray-700">{language.body}</p>
          <div tw="flex gap-2">
            <Button
              loading={busy === 'translate'}
              onClick={() =>
                run(
                  'translate',
                  async () => {
                    const { data } = await translate({
                      variables: { vacancyId, input: {} },
                    });
                    const translated =
                      data?.translateAdTexts?.ad_variants ?? {};
                    // Shown as unsaved changes (D31): the customer checks them and saves.
                    applyToVariants(translated);
                    // The ad on screen shows its new callout now, not after clicking away (5.3).
                    refreshTemplatePreview();
                    addToast({
                      type: ToastTypes.SUCCESS,
                      description: `Texts translated to ${
                        data?.translateAdTexts?.translated_to ??
                        'the campaign language'
                      }. Check them, then save.`,
                    });
                    // The notice stays until the translated texts are SAVED (the editor clears it
                    // then). Clearing it here, as before, lost the warning when the customer left
                    // without saving -- Mark's run, 28 Sep 2026.
                    onTranslated();
                  },
                  'The texts could not be translated. Nothing was changed.'
                )
              }
            >
              {language.translateLabel}
            </Button>
          </div>
        </Box>
      )}
      {phrases && (
        <div tw="p-3 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-700">
          {phrases}
        </div>
      )}
    </div>
  );
};

const Box: React.FC = ({ children }) => (
  <div tw="flex flex-col gap-2 p-4 rounded-md border border-amber-200 bg-amber-50">
    {children}
  </div>
);

export default CampaignValuesMessages;
